import { UseLoading } from "@/common/composition/loading";
import { ICONS } from "@/common/icons.map";
import {
  InformationType,
  POPULATION_TYPE,
  PopulationTypeHelper,
  SensitivityLevelHelper,
} from "@/common/models";
import { SharedState } from "@/common/state";
import { clone, emptyFn, merge_by } from "@/fp";
import { computed, ref, watch } from "@vue/composition-api";
import * as api from "./api";
import { UseModal } from "@/components/root/modal";
import EntitiesTableModal from "@/components/modals/entities-table-modal/EntitiesTableModal.vue";
import { get_modal_props } from "./modals.map";
import { UseDropdownItems } from "@/common/composition/dropdown-items";
const subs = {
  time_frame: emptyFn(),
};

const _selected_sensitivity = ref(3);
const selected_sensitivity = computed(() => _selected_sensitivity.value);

const toggle_sensitivity = async (val: number) => {
  _selected_sensitivity.value = val;
  await load_data();
};

const [info_dirs, selected_info_dir, _set_selected_info_dir] = UseDropdownItems(
  ["information", "internal information"].map((d) => ({
    id: d,
    name: d,
  }))
);

const set_selected_info_dir = async (val: any) => {
  _set_selected_info_dir(val);
  await load_data();
};

const [is_loading, set_loading] = UseLoading(false).as_arr();
const BASE_RESULTS = [
  {
    name: "Shared By Employees",
    icon: "ExposedBy",
    prefix: "shared",
    type: POPULATION_TYPE.EMPLOYEE,
    items: [],
  },
  {
    name: "Shared To Employees",
    icon: "ExposedTo",
    prefix: "received",
    type: POPULATION_TYPE.EMPLOYEE,
    items: [],
  },
  {
    name: "Shared To External Contacts",
    type: POPULATION_TYPE.CONTACT,
    icon: ICONS.ENTITIES.get(POPULATION_TYPE.CONTACT),
    prefix: "received",
    items: [],
  },
  {
    name: "Shared To Organizations",
    type: POPULATION_TYPE.ORGANIZATION,
    icon: ICONS.ENTITIES.get(POPULATION_TYPE.ORGANIZATION),
    prefix: "received",
    items: [],
  },
];
const results = ref(clone(BASE_RESULTS, true));

// @ts-ignore
const merge_items_by_id = merge_by("id");

const filter_sensitivity = (label: InformationType) => {
  if (selected_sensitivity.value === 0) {
    return [0, 1].includes(
      SensitivityLevelHelper.floatToInt(label.sensitivity)
    );
  } else {
    return (
      selected_sensitivity.value ===
      SensitivityLevelHelper.floatToInt(label.sensitivity)
    );
  }
};

const get_api_calls = async () => {
  const labels = SharedState.virtualLabels.value
    .filter((f) => filter_sensitivity(f))
    .map((d) => d.id);
  const is_virtual = selected_info_dir.value?.id === "information";
  const calls_arr = [
    // api.from_cloud_folders({ labels, is_virtual }),
    api.from_employees({ labels, is_virtual }),
    api.to_employees({ labels, is_virtual }),
    api.to_external({ labels, is_virtual }),
    api.to_organization({ labels, is_virtual }),
  ];

  return await Promise.all(calls_arr);
};

const load_data = async () => {
  set_loading(true);
  const base_l0_arr = SharedState.l0_known_content_info_types.value.map(
    (l0) => {
      return {
        id: l0.id,
        name: l0.labelName,
        count: 0,
      };
    }
  );
  const merge_with_base_l0_arr = merge_items_by_id(base_l0_arr);
  const res = await get_api_calls();
  if (res.every((d) => d.success)) {
    // @ts-ignore
    results.value = BASE_RESULTS.map((item, index) => {
      return {
        ...item,
        available: true,
        items: merge_with_base_l0_arr(
          // @ts-ignore
          res[index].value
        )
          .map((d: any) => {
            return {
              // @ts-ignore
              ...d,
              order: SharedState.allInfoTypes.value[d.id].l0Order || 1,
              prefix: item.prefix,
              disable: false,
              index,
            };
          })
          .sort(
            (a: { order: number }, b: { order: number }) => a.order - b.order
          ),
      };
    });
  }
  set_loading(false);
};

const on_item_clicked = async (item: any) => {
  console.log("ITEM:::", item);
  SharedState.ifIsPayingUser(
    async () => {
      const label = SharedState.getInfoType(item.id)!;
      if (SharedState.restrictions.is_restricted_info(label.code)) {
        SharedState.restrictions.show_restrictions_modal(
          "info",
          label.labelName
        );
        return;
      }
      if (SharedState.restrictions.is_restricted_entity(item.type)) {
        SharedState.restrictions.show_restrictions_modal(
          "entity",
          PopulationTypeHelper.asString(item.type, true)
        );
        return;
      }
      const sensitivities = Array(4 - selected_sensitivity.value)
        .fill({})
        .map((_, index) => 3 - index)
        .reverse();
      const props = get_modal_props(item, sensitivities, item.axis_type);
      await UseModal().show(EntitiesTableModal, props);
    },
    { premium_modal_key: "where-popup-2024" }
  );
};

const init = async () => {
  results.value = clone(
    BASE_RESULTS.filter((_, index) => {
      if (SharedState.data_sources_contain_only_gmail.value && index == 0) {
        return false;
      }
      return true;
    })
  );
  await load_data();
  subs.time_frame = watch(
    SharedState.selectedTimeFrameDisplayName,
    async () => {
      await load_data();
    },
    { lazy: true }
  );
};

const destroy = () => {
  Object.values(subs).forEach((unsub) => unsub());
};

export const state = {
  init,
  is_loading,
  destroy,
  results,
  on_item_clicked,
  selected_sensitivity,
  toggle_sensitivity,
  info_dirs,
  selected_info_dir,
  set_selected_info_dir,
};
